/**
 * Defera royal blue — single source of truth.
 *
 * ROYAL (#0A1233, brand-900): header washes, section cards, dark gradients.
 * ROYAL MID (#0F1952, brand-800): interactive UI on dark surfaces — same family
 *   as the header, visible without the old electric vibrant blue (#1025B2).
 */

export const BRAND_ROYAL_HEX = '#0A1233';
export const BRAND_ROYAL_HSL = '228 67% 12%';
export const BRAND_ROYAL_RGB = '10, 18, 51';

export const BRAND_ROYAL_MID_HEX = '#0F1952';
export const BRAND_ROYAL_MID_HSL = '228 68% 19%';
export const BRAND_ROYAL_MID_RGB = '15, 25, 82';

/** @deprecated Use BRAND_ROYAL_MID_* — kept for gradual migration */
export const BRAND_ACCENT_HEX = BRAND_ROYAL_MID_HEX;
export const BRAND_ACCENT_HSL = BRAND_ROYAL_MID_HSL;
export const BRAND_ACCENT_RGB = BRAND_ROYAL_MID_RGB;

/** Muted royal for section-band icons and labels on dark backgrounds. */
export const BRAND_ICON_CLASS = 'text-brand-400';

/** Section card shell — same royal wash family as the trip modal header. */
export const SECTION_CARD_SHELL =
  'bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/45';

/** List row dividers inside section cards (Carts, Explore results, etc.). */
export const BRAND_LIST_DIVIDER = 'divide-y divide-brand-800/35';

/** Review / checkout section cards (Flight Summary, Price Calculator, etc.). */
export const REVIEW_SECTION_CARD =
  `${SECTION_CARD_SHELL} rounded-3xl overflow-hidden mb-8`;

/** Admin cart detail + Carts list section cards (rounded-2xl, no bottom margin). */
export const CART_SECTION_CARD =
  `${SECTION_CARD_SHELL} rounded-2xl overflow-hidden`;

/** Nested panel inside a section card (collapsibles, sub-breakdowns). */
export const BRAND_NESTED_PANEL =
  'rounded-2xl border border-brand-800/40 bg-brand-900/20 overflow-hidden';

/** Nested row inside section cards (segment blocks, passenger rows). */
export const BRAND_NESTED_ROW =
  'rounded-xl border border-brand-800/40 bg-brand-900/20';

/** Primary button gradient — royal mid into royal anchor (matches header family). */
export const BRAND_BTN_GRADIENT_FROM = 'hsl(228, 68%, 19%)';
export const BRAND_BTN_GRADIENT_TO = 'hsl(228, 67%, 12%)';
export const BRAND_BTN_GRADIENT_FROM_HOVER = 'hsl(228, 68%, 22%)';
export const BRAND_BTN_GRADIENT_TO_HOVER = 'hsl(228, 67%, 14%)';
export const BRAND_BTN_BORDER = 'hsl(228, 40%, 28%)';
export const BRAND_BTN_BORDER_HOVER = 'hsl(228, 40%, 34%)';

/** Selected toggle/pill (deposit tier, payment plan on Review). */
export const BRAND_SELECTED_TOGGLE_CLASS =
  'bg-gradient-to-b from-[hsl(228,68%,19%)] to-[hsl(228,67%,12%)] ' +
  'border border-[hsl(228,40%,28%)]/50 text-white ' +
  'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]';

/** Shared primary-action button skin (schedule CTAs, modal submits). */
export const BRAND_PRIMARY_BTN_CLASS =
  'font-medium text-white transition-colors duration-200 ' +
  'bg-gradient-to-b from-[hsl(228,68%,19%)] to-[hsl(228,67%,12%)] ' +
  'border border-[hsl(228,40%,28%)]/50 ' +
  'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_1px_2px_rgba(0,0,0,0.25)] ' +
  'hover:from-[hsl(228,68%,22%)] hover:to-[hsl(228,67%,14%)] ' +
  'hover:border-[hsl(228,40%,34%)]/55 ' +
  'hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.10),0_2px_8px_rgba(10,18,51,0.45)] ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

/** Horizontal booking-option cards (Flight Options fare picker). */
export const BRAND_OPTION_CARD_UNSELECTED =
  'bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 ' +
  'border border-white/15 hover:border-white/25 ' +
  'ring-1 ring-brand-800/35 hover:ring-brand-700/45 backdrop-blur-sm ' +
  'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]';

export const BRAND_OPTION_CARD_SELECTED =
  'bg-gradient-to-b from-[hsl(228,68%,19%)] to-[hsl(228,67%,12%)] ' +
  'border-2 border-brand-600/65 text-white ' +
  'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.10),0_4px_20px_rgba(10,18,51,0.45)]';

/** Trips dashboard / Rewards section cards (no bottom margin). */
export const REWARDS_SECTION_CARD =
  `${SECTION_CARD_SHELL} rounded-3xl overflow-hidden`;

/** Inline trip row on Dashboard (Trips list). */
export const TRIPS_LIST_ITEM_SHELL =
  `${SECTION_CARD_SHELL} rounded-2xl sm:rounded-3xl transition-all duration-300 ` +
  'hover:border-brand-700/45 hover:from-brand-900/65 ' +
  'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]';

/** Dashboard search + filter controls. */
export const DASHBOARD_FILTER_INPUT =
  'bg-brand-900/35 border-brand-800/45 text-white placeholder:text-white/35 ' +
  'hover:bg-brand-900/45 focus-visible:ring-1 focus-visible:ring-brand-600/30 ' +
  'focus-visible:ring-offset-0 focus-visible:border-brand-700/50';

/** Status tab — inactive pill on Trips dashboard. */
export const DASHBOARD_TAB_INACTIVE =
  'bg-brand-900/25 text-white/55 border-brand-800/40 hover:bg-brand-900/40 ' +
  'hover:text-white/85 hover:border-brand-700/45';

/** Status tab — active pill on Trips dashboard. */
export const DASHBOARD_TAB_ACTIVE = BRAND_SELECTED_TOGGLE_CLASS;
